/**
 * Logical functions of the Core_Formula_Set (Requirement 4.1).
 *
 * All of them are `errorTolerant`, so the evaluator hands them their arguments
 * as evaluated — errors included — and each decides for itself. `IF` propagates
 * an error in its condition but happily returns a branch whose sibling errored,
 * `IFERROR`/`IFNA` are the whole point of catching one, and `AND`/`OR`
 * propagate, matching Excel.
 *
 * `AND` and `OR` ignore text and blanks inside ranges and fail with `#VALUE!`
 * when no logical value survives, which is Excel's documented behaviour.
 */

import {
  VARIADIC,
  BLANK,
  bool,
  err,
  isErrCode,
  isError,
  scalarsEqual,
  toBool,
  toScalar,
  type EvalContext,
  type FnSpec,
  type Scalar,
  type Value,
} from '../values.js';
import { collectScalars } from './helpers.js';

/** Truth of a condition argument: the error itself, or a boolean. */
function conditionOf(v: Value | undefined, ctx: EvalContext): boolean | Scalar {
  if (v === undefined) return false;
  const scalar = toScalar(v, ctx);
  if (isError(scalar)) return scalar;
  const b = toBool(scalar);
  return isErrCode(b) ? err(b) : b;
}

function isErrorResult(v: boolean | Scalar): v is Scalar {
  return typeof v !== 'boolean';
}

/** Shared body of `AND` and `OR`. */
function logicalFold(name: 'AND' | 'OR'): FnSpec {
  return {
    name,
    min: 1,
    max: VARIADIC,
    errorTolerant: true,
    fn: (args, ctx) => {
      let seen = 0;
      let result = name === 'AND';
      for (const cell of collectScalars(args, ctx)) {
        if (isError(cell)) return cell;
        // Text and blanks inside a range are skipped, not coerced.
        if (cell.k === 'empty' || cell.k === 'text') continue;
        const b = toBool(cell);
        if (isErrCode(b)) return err(b);
        seen += 1;
        result = name === 'AND' ? result && b : result || b;
      }
      if (seen === 0) return err('#VALUE!');
      return bool(result);
    },
  };
}

export const LOGICAL_FUNCTIONS: readonly FnSpec[] = [
  {
    name: 'IF',
    min: 2,
    max: 3,
    errorTolerant: true,
    fn: (args, ctx) => {
      const condition = conditionOf(args[0], ctx);
      if (isErrorResult(condition)) return condition;
      if (condition) return args[1] ?? BLANK;
      return args[2] ?? bool(false);
    },
  },
  {
    name: 'IFS',
    min: 2,
    max: VARIADIC,
    errorTolerant: true,
    fn: (args, ctx) => {
      if (args.length % 2 !== 0) return err('#VALUE!');
      for (let i = 0; i < args.length; i += 2) {
        const condition = conditionOf(args[i], ctx);
        if (isErrorResult(condition)) return condition;
        if (condition) return args[i + 1] ?? BLANK;
      }
      return err('#N/A');
    },
  },
  {
    name: 'IFERROR',
    min: 2,
    max: 2,
    errorTolerant: true,
    fn: (args, ctx) => {
      const value = toScalar(args[0] as Value, ctx);
      return isError(value) ? (args[1] as Value) : (args[0] as Value);
    },
  },
  {
    name: 'IFNA',
    min: 2,
    max: 2,
    errorTolerant: true,
    fn: (args, ctx) => {
      const value = toScalar(args[0] as Value, ctx);
      return isError(value) && value.v === '#N/A' ? (args[1] as Value) : (args[0] as Value);
    },
  },
  logicalFold('AND'),
  logicalFold('OR'),
  {
    name: 'NOT',
    min: 1,
    max: 1,
    errorTolerant: true,
    fn: (args, ctx) => {
      const condition = conditionOf(args[0], ctx);
      return isErrorResult(condition) ? condition : bool(!condition);
    },
  },
  {
    name: 'SWITCH',
    min: 3,
    max: VARIADIC,
    errorTolerant: true,
    fn: (args, ctx) => {
      const subject = toScalar(args[0] as Value, ctx);
      if (isError(subject)) return subject;
      let i = 1;
      for (; i + 1 < args.length; i += 2) {
        const candidate = toScalar(args[i] as Value, ctx);
        if (isError(candidate)) return candidate;
        if (scalarsEqual(subject, candidate)) return args[i + 1] as Value;
      }
      // A single trailing argument is the default.
      return i < args.length ? (args[i] as Value) : err('#N/A');
    },
  },
];
